import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { SettingsPeopleSection } from '../../../../App/components/SettingsPeopleSection';
import { SettingsWorkspaceName } from '../../../../App/components/SettingsWorkspaceName';
import { SettingsPlanSection } from '../../../../App/components/SettingsPlanSection';
import { NAV, DEFAULT_PREFERENCES } from './constants';
import FilesPane from './components/FilesPane';
import NotificationsPane from './components/NotificationsPane';
import PreferencesPane from './components/PreferencesPane';
import './styles/index.css';

/**
 * SettingsModal Component
 *
 * Modal for adjusting user preferences like trackpad sensitivity and auto-archive.
 * Settings are stored in the backend via the preferences prop.
 */
const SettingsModal = ({ isOpen, onClose, preferences = {}, onPreferencesChange }) => {
  const [pane, setPane] = useState('workspace');
  const [localPreferences, setLocalPreferences] = useState({ ...DEFAULT_PREFERENCES, ...preferences });

  useEffect(() => {
    if (isOpen) {
      setLocalPreferences({ ...DEFAULT_PREFERENCES, ...preferences });
    }
  }, [isOpen, preferences]);

  const handleChange = useCallback((key, value) => {
    setLocalPreferences(prev => {
      const next = { ...prev, [key]: value };
      onPreferencesChange?.(next);
      return next;
    });
  }, [onPreferencesChange]);

  const handleReset = useCallback(() => {
    setLocalPreferences({ ...DEFAULT_PREFERENCES });
    onPreferencesChange?.({ ...DEFAULT_PREFERENCES });
  }, [onPreferencesChange]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const paneTitle = NAV.find((item) => item.id === pane)?.label || 'Settings';

  const modalContent = (
    <div className="settings-modal__overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <aside className="settings-modal__nav" aria-label="Settings">
          <div className="settings-modal__nav-brand">Settings</div>
          <nav className="settings-modal__nav-list">
            {NAV.map((item) => (
              <button
                key={item.id}
                type="button"
                className={`settings-modal__nav-item ${
                  pane === item.id ? 'settings-modal__nav-item--active' : ''
                }`}
                onClick={() => setPane(item.id)}
              >
                {item.label}
              </button>
            ))}
          </nav>
        </aside>

        <div className="settings-modal__main">
          <div className="settings-modal__header">
            <h2 className="settings-modal__title">{paneTitle}</h2>
            <button
              type="button"
              className="settings-modal__close"
              onClick={onClose}
              aria-label="Close settings"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="settings-modal__content">
            {pane === 'workspace' && <SettingsWorkspaceName />}
            {pane === 'people' && <SettingsPeopleSection />}
            {pane === 'plan' && <SettingsPlanSection />}
            {pane === 'files' && <FilesPane />}
            {pane === 'notifications' && (
              <NotificationsPane
                localPreferences={localPreferences}
                onChange={handleChange}
              />
            )}
            {pane === 'preferences' && (
              <PreferencesPane
                localPreferences={localPreferences}
                onChange={handleChange}
                onReset={handleReset}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default SettingsModal;
